import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Empty, FilterGroup, PageHero, SortBar, toggleIn, useQuery } from '../../shared/components/Common';
import Icon from '../../shared/components/Icons';
import { fmtDay, formatPrice } from '../../shared/data/common';
import { useBusSearch } from '../../api/bus';
import {
  busQueryString, daysLater, departureSlots, durationLabel, parseBusQuery, searchProblem, slotOf, timeLabel,
  toBusParams, toSearchRequest,
} from './searchModel';
import './bus.css';

/** Seat map page for a bus; the search stays in the URL so the page survives a refresh. */
export const seatsPath = (search, bus) => `/bus/seats?${busQueryString(search)}&bus=${encodeURIComponent(bus.busId)}`;

const kinds = [
  ['ac', 'AC', (b) => b.isAc],
  ['nonac', 'Non-AC', (b) => !b.isAc],
  ['sleeper', 'Sleeper', (b) => b.isSleeper],
  ['seater', 'Seater', (b) => !b.isSleeper || /seater/i.test(b.busType)],
];

/** Departure → arrival with the journey time between, as on a ticket. */
export function BusTimes({ bus }) {
  const later = daysLater(bus.departure, bus.arrival);
  return (
    <div className="bus-times">
      <div><b>{timeLabel(bus.departure)}</b><span className="muted small">{fmtDay(bus.departure.slice(0, 10)).replace(/ \d{4}$/, '')}</span></div>
      <span className="bus-dur"><span>{durationLabel(bus.durationMinutes)}</span></span>
      <div><b>{timeLabel(bus.arrival)}{later > 0 && <sup className="text-coral"> +{later}</sup>}</b><span className="muted small">{fmtDay(bus.arrival.slice(0, 10)).replace(/ \d{4}$/, '')}</span></div>
    </div>
  );
}

function BusCard({ bus, onSelect }) {
  return (
    <article className="bus-card">
      <div className="bus-info">
        <h3>{bus.operator}{bus.topRated && <span className="tag">Top rated</span>}</h3>
        <span className="muted small">{bus.busType}</span>
        <div className="transfer-meta">
          <span><Icon name="user" size={14} /> {bus.seatsAvailable} seats left</span>
          {bus.isAc && <span><Icon name="snow" size={14} /> AC</span>}
          {bus.mTicketAllowed && <span><Icon name="mobile" size={14} /> m-Ticket</span>}
          {bus.partialCancellationAllowed && <span><Icon name="check" size={14} /> Partial cancellation</span>}
        </div>
        {bus.amenities.length > 0 && (
          <div className="bus-amenities">{bus.amenities.slice(0, 5).map((a) => <span key={a} className="tag">{a}</span>)}{bus.amenities.length > 5 && <span className="muted small">+{bus.amenities.length - 5} more</span>}</div>
        )}
      </div>
      <BusTimes bus={bus} />
      <div className="row-price">
        <span className="muted small">Starts from</span>
        <b className="price big">{formatPrice(bus.fareFrom)}</b>
        {bus.fareTo > bus.fareFrom && <span className="muted small">up to {formatPrice(bus.fareTo)}</span>}
        <button type="button" className="btn btn-primary" onClick={onSelect} disabled={bus.seatsAvailable === 0}>
          {bus.seatsAvailable === 0 ? 'Sold out' : 'Select seats'}
        </button>
      </div>
    </article>
  );
}

export default function BusList() {
  const q = useQuery();
  const navigate = useNavigate();
  const search = parseBusQuery(q);
  const problem = searchProblem(search);
  const { loading, data, error, retry } = useBusSearch(problem ? null : search, toSearchRequest);
  const [sort, setSort] = useState('price');
  const [types, setTypes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [operators, setOperators] = useState([]);
  const [showAllOperators, setShowAllOperators] = useState(false);

  const buses = useMemo(() => data?.buses || [], [data]);
  const operatorList = useMemo(() => {
    const counts = new Map();
    buses.forEach((b) => counts.set(b.operator, (counts.get(b.operator) || 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [buses]);
  const list = buses
    .filter((b) => types.every((t) => kinds.find(([k]) => k === t)[2](b)))
    .filter((b) => !slots.length || slots.includes(slotOf(b.departure)))
    .filter((b) => !operators.length || operators.includes(b.operator))
    .sort((a, b) => {
      if (sort === 'departure') return a.departure.localeCompare(b.departure);
      if (sort === 'duration') return (a.durationMinutes || 1e9) - (b.durationMinutes || 1e9);
      if (sort === 'seats') return b.seatsAvailable - a.seatsAvailable;
      return a.fareFrom - b.fareFrom;
    });

  const route = `${search.from || 'From'} → ${search.to || 'To'}`;
  const summary = {
    tag: 'Bus',
    items: [['From', search.from || '—'], ['To', search.to || '—'], ['Journey date', fmtDay(search.date)]],
  };
  const clear = () => { setTypes([]); setSlots([]); setOperators([]); };

  return (
    <>
      <PageHero tab="bus" title={`Buses: ${route}`} query={toBusParams(search)} summary={summary} subtitle={fmtDay(search.date)} />
      <div className="container listing">
        <aside className="filters">
          <div className="filters-head"><b>Filters</b>
            <button type="button" className="link small" onClick={clear}>Clear all</button>
          </div>
          <FilterGroup title="Bus type">
            {kinds.map(([k, label, test]) => (
              <Check key={k} label={label} checked={types.includes(k)} onChange={(on) => setTypes(toggleIn(types, k, on))}
                extra={String(buses.filter(test).length)} />
            ))}
          </FilterGroup>
          <FilterGroup title="Departure time">
            {departureSlots.map(([k, label]) => (
              <Check key={k} label={label} checked={slots.includes(k)} onChange={(on) => setSlots(toggleIn(slots, k, on))}
                extra={String(buses.filter((b) => slotOf(b.departure) === k).length)} />
            ))}
          </FilterGroup>
          <FilterGroup title="Operators">
            {operatorList.length ? (showAllOperators ? operatorList : operatorList.slice(0, 8)).map(([name, count]) => (
              <Check key={name} label={name} checked={operators.includes(name)} onChange={(on) => setOperators(toggleIn(operators, name, on))} extra={String(count)} />
            )) : <span className="muted small">—</span>}
            {operatorList.length > 8 && (
              <button type="button" className="link small" onClick={() => setShowAllOperators(!showAllOperators)}>
                {showAllOperators ? 'Show fewer' : `Show all ${operatorList.length}`}
              </button>
            )}
          </FilterGroup>
        </aside>

        <div className="results">
          {problem && <div className="api-message error" role="alert">{problem} Use <b>Modify Search</b> above.</div>}
          {!problem && loading && (
            <div className="api-message" role="status"><span className="spinner sm" /> Finding buses from {search.from} to {search.to}…</div>
          )}
          {!problem && error && (
            <div className="api-message error" role="alert">
              <p>{error}</p>
              <button type="button" className="btn btn-outline btn-sm" onClick={retry}>Try again</button>
            </div>
          )}
          {data?.warnings?.map((w) => <div key={w} className="api-message warning">{w}</div>)}
          {data && (
            <>
              <SortBar count={list.length} label={`bus${list.length === 1 ? '' : 'es'}`} sort={sort} setSort={setSort}
                options={[['price', 'Cheapest'], ['departure', 'Departure'], ['duration', 'Fastest'], ['seats', 'Most seats']]} />
              {list.length === 0 && (
                <Empty text={buses.length ? 'No buses match your filters.' : 'No buses are available on this route for the date. Try another date or a nearby city.'} />
              )}
              <div className="transfer-list">
                {list.map((bus) => (
                  <BusCard key={bus.busId} bus={bus}
                    onSelect={() => navigate(seatsPath(search, bus), { state: { searchId: data.searchId, bus } })} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
